/**
 * "Recently viewed" for the Reports page (AWS cost plan Phase 3, Task 13).
 *
 * Kept in this browser's localStorage, newest first, as catalog tool ids. It is
 * a convenience only: a private window or cleared site data just shows an empty
 * row. Stars, which must survive signing out, use the server-side pins instead.
 *
 * `recordReportVisit` is called from Layout on every route change; it records
 * only routes that belong to the Reports department.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { activeToolForPath } from '@/lib/toolRouting';

const STORAGE_KEY = 'cockpit.reports.recent';
export const MAX_RECENT_REPORTS = 6;

export function readRecentReports(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Pure list update, exported for the unit test. */
export function pushRecent(list: string[], toolId: string): string[] {
  return [toolId, ...list.filter((id) => id !== toolId)].slice(0, MAX_RECENT_REPORTS);
}

export function recordReportVisit(toolId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pushRecent(readRecentReports(), toolId)));
  } catch {
    // Storage unavailable: the Recently viewed row simply stays empty.
  }
}

/** Records the current route when it is a report. Call once, from Layout. */
export function useRecordReportVisits(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const active = activeToolForPath(pathname);
    if (active?.deptId === 'reports') recordReportVisit(active.toolId);
  }, [pathname]);
}
